/**
 * Bulk download.
 *
 * §23 said the user asks, is told it is being prepared, and is free to leave
 * the page — because a worker was building the archive. Encryption ended that
 * arrangement: a worker holds no group key, so the zip it produced would be a
 * valid archive of unopenable files. `exports.create` refuses sealed files for
 * exactly that reason, which by now means it refuses everything.
 *
 * So the archive is built here instead, and the one promise that has to be
 * withdrawn is leaving the page. That is stated in the dock rather than
 * discovered: a download that silently dies when somebody navigates away is
 * worse than one that says it needs the tab open.
 *
 * The state shape is unchanged, so the dock did not have to learn anything new.
 */

import { useCallback, useRef, useState } from "react";
import { errorMessage } from "../../lib/trpc.js";
import { downloadAsZip } from "./browserZip.js";

export interface ExportState {
  exportId: string | null;
  state: "idle" | "queued" | "active" | "ready" | "failed" | "expired";
  progress: number;
  itemCount: number;
  totalBytes: number;
  url: string | null;
  error: string | null;
  /** Photographs left out, with why — an archive of 97 of 100 must say so. */
  skipped: { name: string; why: string }[];
}

const IDLE: ExportState = {
  exportId: null,
  state: "idle",
  progress: 0,
  itemCount: 0,
  totalBytes: 0,
  url: null,
  error: null,
  skipped: [],
};

export function useExport(groupId: string) {
  const [state, setState] = useState<ExportState>(IDLE);
  const abort = useRef<AbortController | null>(null);

  const start = useCallback(
    async (
      files: { id: string; name: string; sizeBytes?: number }[],
      variant: "preview" | "original" = "preview",
    ) => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;

      setState({ ...IDLE, state: "active", itemCount: files.length });

      try {
        const result = await downloadAsZip(
          groupId,
          files,
          variant,
          (progress) => {
            setState((current) => ({
              ...current,
              progress: Math.round((progress.done / progress.total) * 100),
              skipped: progress.skipped,
            }));
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;

        /*
         * Nothing done means the save dialog was dismissed, which is a decision
         * rather than a failure — the dock closes without saying anything.
         */
        if (result.done === 0 && result.skipped.length === 0) {
          setState(IDLE);
          return;
        }

        setState((current) => ({
          ...current,
          state: "ready",
          progress: 100,
          skipped: result.skipped,
        }));
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({ ...IDLE, state: "failed", error: errorMessage(error) });
      }
    },
    [groupId],
  );

  const dismiss = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setState(IDLE);
  }, []);

  /**
   * Nothing to do.
   *
   * The file is written as it is built — either straight to disk, or handed to
   * the browser the moment the last entry goes in — so by the time the dock
   * says "ready" the download has already happened. Kept because the dock's
   * shape has a save button for the old two-step flow, and removing it is a
   * separate change from this one.
   */
  const save = useCallback(() => undefined, []);

  return { ...state, start, save, dismiss };
}
